import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BoardCustomContainer, ReportSlideFragmentType } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BoardWallComponent from 'webapp/components/board/wall';
import ReportsSkeletonFragmentComponent from 'webapp/components/reports/skeleton-fragment';

import BoardState from 'webapp/models-custom/board-state';
import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';

import { reportDataboardsMDRChangedEventName } from 'webapp/services/event-manager';

interface Args {
	model: ReportSlideFragment;
	onBoardPreviewHashChange?: (boardId: string, previewHash?: string) => void;
	width?: number;
}

export default class ReportsViewerFragmentBoardComponent<T extends Args> extends Component<T> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('event-manager') declare eventManagerService: Service.EventManager;

	@tracked boardState: BoardState | null = null;

	@tracked previewHash: string | null = null;

	masterDateRangeEventName = reportDataboardsMDRChangedEventName(this.args.model.id);

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// this.onModelChange calls an endpoint which is not accesible with token
		// delay is needed to avoid reaching that endpoint before data is loaded
		// board data is loaded in datawall controller -> loadAllReportSlidesData
		if (!this.navigationService.isDatawallApp || !this.args.model.isOnPagelessReport) {
			later(() => {
				this.onModelChange(true);
			}, 100);
		} else {
			this.onModelChange(true);
		}
	}

	get isEmpty(): boolean {
		return !this.boardOrPreview && !this.boardState?.forceLoader;
	}

	get boardOrPreview() {
		const previewHash = this.previewHash || this.args.model.previewHash;
		return this.args.model.getBoardOrPreviewForHash(previewHash);
	}

	get customContainer(): BoardCustomContainer {
		return this.args.model.isOnPagelessReport
			? BoardCustomContainer.REPORT_PAGELESS
			: BoardCustomContainer.REPORT_SLIDE;
	}

	get emptyBoardStyles(): string {
		if (!this.args.width) {
			return '';
		}

		const height = (this.args.width * 9) / 16;
		return `width: ${this.args.width}px; height: ${height}px;`;
	}

	get skeletonType(): ReportSlideFragmentType {
		return ReportSlideFragmentType.BOARD;
	}

	@action
	handleModelChangeEvent() {
		this.onModelChange();
	}

	@action
	onModelChange(isInitialCheck = false): void {
		let boardState;
		const model = this.args.model;

		if (isInitialCheck) {
			this.previewHash = model.previewHash;
		}

		const board = this.boardOrPreview;

		if (!isInitialCheck) {
			const currentBoardState = this.boardState;
			if (
				currentBoardState &&
				board &&
				currentBoardState.board === board &&
				currentBoardState.board.previewHash === board.previewHash
			) {
				return;
			}
		}

		const fragment = this.args.model;
		const report = fragment.slide?.report;

		if (board) {
			boardState = BoardState.createFromBoard(board);

			if (!fragment.previewHash) {
				let boardId: string | null;
				if (board.isPreview) {
					boardId = String(board.boardId);
				} else {
					boardId = board.id;
				}
				if (boardId && !this.mmBoardsService.isBoardDataRequested(boardId)) {
					void this.mmBoardsService.requestBoardData(boardId).then(() => {
						window.dispatchEvent(new Event('resize'));
					});
				}
			}
		} else if (fragment?.previewHash) {
			// maybe not requested yet
			void this.mmBoardPreviewsService
				.requestPreview(fragment.previewHash, fragment.boardId!)
				.then(() => {
					if (this.boardOrPreview) {
						this.onModelChange();
					} else {
						// seems like preview hash was invalid, remove it
						fragment.updatePreviewHash('');
					}
				});

			// meanwhile, use the original board
			const originalBoard = model.board;
			if (originalBoard) {
				// this can be missing bcos of veeeery fast slide switching
				boardState = BoardState.createFromBoard(originalBoard);
				boardState.forceLoader = true;
			}
		} else if (!this.isEmpty && report?.isTemplate) {
			const token = report?.token;
			if (token && model.slide?.id) {
				// board from template, so no instance of board available (unless user opened popup before)
				// in this case, request slide data on the "public" URL
				this.mmReportSlidesService
					.getReportSlideDataWithToken(token, model.slide.id)
					.then((_reply) => {
						// this.onModelChange();
					})
					.catch(() => {
						// skip error
					});
			}
		}

		this.boardState = boardState || null;

		if (boardState && board) {
			boardState.onPreviewHashChange = (previewHash) => {
				this.previewHash = previewHash;
				this.args.onBoardPreviewHashChange?.(
					board.boardIdForPublicUrl,
					previewHash || undefined,
				);
			};
		}
	}

	@action
	setupMasterDateRangeEvent() {
		this.eventManagerService.on(this.masterDateRangeEventName, this, (eventData) => {
			this.boardState?.updateMasterDateRangeSettings(eventData.dateRange, null);
		});
	}

	@action
	teardownMasterDateRangeEvent() {
		this.eventManagerService.off(this.masterDateRangeEventName, this);
	}
	<template>
		<div
			class="fragment fragment--board {{if this.isEmpty 'fragment--board--empty'}}"
			{{didInsert this.setupMasterDateRangeEvent}}
			{{didUpdate this.handleModelChangeEvent this.boardOrPreview}}
			{{willDestroy this.teardownMasterDateRangeEvent}}
			style={{this.emptyBoardStyles}}
		>
			{{#if this.isEmpty}}
				<svg>
					<use
						{{! @glint-ignore: xlink:href is not typed on SVGUseElement in Glint 2 }}
						xlink:href="/images/reports/slide-type-board.svg#img"
						width="100%"
						height="100%"
					></use>
				</svg>
				<span>
					Missing dashboard
				</span>
			{{else if this.boardState}}
				<BoardWallComponent
					@boardState={{this.boardState}}
					@inCustomContainer={{this.customContainer}}
					@trackingOrigin="report_preview"
				/>
			{{else}}
				<ReportsSkeletonFragmentComponent @type={{this.skeletonType}} />
			{{/if}}
		</div>
	</template>
}
